'use client'

import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { memo, useMemo } from 'react'

import { SvdProductCard } from '@src/components/svd-product-card'
import { isPlSvdCard, mapPlSvdCard, type PlSvdCard } from '@src/components/svd-product-card/map-pl-svd-card'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { ProductCard } from '@src/ds/blocks/product-card'
import { SelectedFilter } from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import NoProduct from '@src/pages/[...slug]/components/collection/molecules/noProducts/NoProduct'
import {
  getTrackingMetaData,
  PRODUCT_VIEW_TRACKER,
} from '@src/pages/search/searchResultsSection/searchResults/searchResults.constants'
import type { SearchProductCard } from '@src/pages/search/searchResultsSection/utils'
import { useIsSvdEnabled } from '@src/quince-site-common/src/hooks/use-is-svd-enabled'
import { FINDING_METHOD_AGGREGATE, setFindingMethod } from '@src/telemetry/findingMethod'

const sectionShellClass = css({
  width: 'full',
  px: { base: 'space-16', lg: 'space-48' },
})

const sectionInnerClass = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: 'full',
  maxWidth: '1504px',
  marginInline: 'auto',
  pb: { base: 'space-80', lg: 'space-100' },
})

const gridClass = css({
  display: 'grid',
  width: 'full',
  gridTemplateColumns: {
    base: 'repeat(2, minmax(0, 1fr))',
    lg: 'repeat(4, minmax(0, 1fr))',
  },
  columnGap: { base: 'space-16', lg: 'space-24' },
  rowGap: { base: 'space-32', lg: 'space-48' },
})

const noResultsInnerClass = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  width: 'full',
  maxWidth: '1504px',
  marginInline: 'auto',
  minHeight: { base: '200px', lg: '260px' },
})

const noResultsTitleClass = css({
  textStyle: { base: 'title-14', lg: 'title-18' },
  textAlign: 'center',
  width: 'full',
})

const noResultsBodyClass = css({
  width: 'full',
  textAlign: 'center',
  marginTop: 'space-8',
})

export interface SearchResultsContentDSProps {
  productCards: SearchProductCard[]
  searchQuery: string
  onResetFilters: () => void
  selectedFilters: SelectedFilter
}

// Identical for every search tile, so it lives at module scope instead of being re-created per card
// (a fresh arrow per card would defeat ProductCard's memo). Matches legacy: sets the global
// finding-method aggregate; intentionally NOT added to the tracked event context.
const handleSearchProductClick = () => {
  setFindingMethod[FINDING_METHOD_AGGREGATE.SEARCH]?.()
}

interface SearchResultProductCardProps {
  productCard: SearchProductCard
  index: number
  searchQuery: string
  /* TEMPORARY (STRFRNT-15810/STRFRNT-16013) — see `SVD_ENRICH_VERSION_HEADER` doc. Passed down from the
   * parent rather than read per-card, so N tiles don't produce N Statsig exposures / store subscriptions. */
  isSvdEnabled: boolean
}

// Memoized per-card wrapper. The grid maps over `productCards`, so the per-card
// `productClickTrackConfig` cannot be `useMemo`'d in the loop; it's built here from stable primitive
// inputs behind a `memo` boundary so the wrapped ProductCard's shallow-compare skips re-renders on
// unrelated parent state. `index` is a dep so the tracked position stays correct if results reorder.
const SearchResultProductCard = memo(function SearchResultProductCard({
  productCard,
  index,
  searchQuery,
  isSvdEnabled,
}: SearchResultProductCardProps) {
  const productClickTrackConfig = useMemo(
    () => ({
      trackInternal: true,
      // eslint-disable-next-line i18next/no-literal-string -- telemetry category, not user-facing copy
      category: 'search_results_collection',
      // eslint-disable-next-line i18next/no-literal-string -- telemetry action, not user-facing copy
      action: 'search_by_query',
      // Parity with the legacy click context (search-results-content-legacy.tsx):
      // searchQuery / position / product_position / getTrackingMetaData().context only.
      // `findingMethod` is intentionally NOT sent here — the global aggregate is still
      // set via `onClickCb` (matching legacy), but adding it to the event context
      // would be a net-new telemetry property vs the legacy path.
      context: {
        searchQuery,
        position: index,
        product_position: index,
        ...getTrackingMetaData().context,
      },
    }),
    [searchQuery, index],
  )

  // TEMPORARY (STRFRNT-15810/STRFRNT-16013 · search SVD preview): `isPlSvdCard` shape-guards on
  // anchor data, so a legacy-shaped card (the enrich header is off, or PL hasn't enriched this hit)
  // always falls through to the DS `ProductCard` below. Never dereference legacy-typed fields
  // (`getInitialSelectedVariant`, `CardVariant.displayConfig`) on this branch — `displayConfig` is
  // null at runtime for no-VDA SVD products.
  if (isSvdEnabled && isPlSvdCard(productCard.product)) {
    const svdProduct = mapPlSvdCard(productCard.product as unknown as PlSvdCard)
    return (
      <SvdProductCard
        product={svdProduct}
        productViewTracker={PRODUCT_VIEW_TRACKER}
        // Baked in per-card at fetch time (`transformProductsToProductCards`) — NOT the sitewide
        // "latest" queryId, which pagination overwrites. Reading the global value here would pair an
        // old card's objectID with a newer search's queryId once load-more has run (review finding).
        searchAlgoliaQueryId={productCard.searchAlgoliaQueryId ?? ''}
        productSearchObjectId={svdProduct.searchObjectId ?? undefined}
        productPosition={index + 1}
        searchQuery={searchQuery}
        isPageEligibleForHolidayTag
        onClickCb={handleSearchProductClick}
        productClickTrackConfig={productClickTrackConfig}
      />
    )
  }

  return (
    <ProductCard
      productCard={productCard}
      tracker={PRODUCT_VIEW_TRACKER}
      productViewTracker={PRODUCT_VIEW_TRACKER}
      searchQuery={searchQuery}
      position={index + 1}
      isPageEligibleForHolidayTag
      onClickCb={handleSearchProductClick}
      productClickTrackConfig={productClickTrackConfig}
      // eslint-disable-next-line i18next/no-literal-string -- DS ProductCard variant token, not user-facing copy
      dsVariant="default"
    />
  )
})

SearchResultProductCard.displayName = 'SearchResultProductCard'

export const SearchResultsContentDS = ({
  productCards,
  searchQuery,
  onResetFilters,
  selectedFilters,
}: SearchResultsContentDSProps) => {
  const { t } = useTranslation()
  const hasAppliedFilters = SelectedFilter.isAnyFilterSelected(selectedFilters)
  // TEMPORARY (STRFRNT-15810/STRFRNT-16013) — see `SVD_ENRICH_VERSION_HEADER` doc. Read once per grid,
  // not per card, so N tiles don't produce N Statsig exposures. The card shape guard below
  // (`isPlSvdCard`) only ever returns true when PL actually sent SVD-shaped data, which is exactly
  // what `useSearchResults.ts`'s fetch-header decision (same flag) controls.
  const isSvdEnabled = useIsSvdEnabled()

  if (productCards.length > 0) {
    return (
      <div className={sectionShellClass}>
        <div className={sectionInnerClass}>
          <div className={gridClass}>
            {productCards.map((productCard, pIdx) => (
              <SearchResultProductCard
                key={`${productCard.product.productId}_${pIdx}`}
                productCard={productCard}
                index={pIdx}
                searchQuery={searchQuery}
                isSvdEnabled={isSvdEnabled}
              />
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={sectionShellClass}>
      <div className={noResultsInnerClass}>
        {!hasAppliedFilters && <h1 className={noResultsTitleClass}>{`"${searchQuery.toUpperCase()}"`}</h1>}
        <div className={noResultsBodyClass} data-testid={TEST_LOCATORS.NO_SEARCH_RESULTS}>
          {hasAppliedFilters ? (
            <NoProduct onResetFilters={onResetFilters} isFiltersApplied={hasAppliedFilters} />
          ) : (
            <span
              className={css({
                textStyle: 'body-14',
              })}
            >
              {t('no_search_results')}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
